import { useState, useRef } from "react";
import { ImportIcon } from "components/atoms/Icon";
import posthog from "posthog-js";

interface SimpleCsvUploaderProps {
    onResults: (results: any[]) => void;
    importIdentifier: string;
    onError?: (error: string) => void;
    importButtonStyle?: "primary" | "secondary";
    rowHooks?: ((record: any) => Promise<any>)[];
}

function SimpleCsvUploader({
    onResults,
    importIdentifier,
    onError,
    importButtonStyle = "primary",
    rowHooks = [],
}: SimpleCsvUploaderProps) {
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const parseCSV = (text: string): string[][] => {
        const rows: string[][] = [];
        let currentRow: string[] = [];
        let currentField = "";
        let insideQuotes = false;

        for (let i = 0; i < text.length; i += 1) {
            const char = text[i];
            const nextChar = text[i + 1];

            if (char === '"') {
                if (insideQuotes && nextChar === '"') {
                    currentField += '"';
                    i += 1;
                } else {
                    insideQuotes = !insideQuotes;
                }
            } else if (char === "," && !insideQuotes) {
                currentRow.push(currentField.trim());
                currentField = "";
            } else if ((char === "\n" || char === "\r") && !insideQuotes) {
                if (currentField || currentRow.length > 0) {
                    currentRow.push(currentField.trim());
                    if (currentRow.some((field) => field)) {
                        rows.push(currentRow);
                    }
                    currentRow = [];
                    currentField = "";
                }
                if (char === "\r" && nextChar === "\n") {
                    i += 1;
                }
            } else {
                currentField += char;
            }
        }

        if (currentField || currentRow.length > 0) {
            currentRow.push(currentField.trim());
            if (currentRow.some((field) => field)) {
                rows.push(currentRow);
            }
        }

        return rows;
    };

    const csvToObjects = (rows: string[][]): any[] => {
        if (rows.length === 0) {
            return [];
        }

        const headers = rows[0];
        const dataRows = rows.slice(1);

        return dataRows
            .filter((row) => row.some((field) => field.trim()))
            .map((row) => {
                const obj: any = {};
                headers.forEach((header, index) => {
                    obj[header] = row[index] || "";
                });
                return obj;
            });
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsLoading(true);
        try {
            const text = await file.text();
            const rows = parseCSV(text);
            const records = csvToObjects(rows);

            const processedRecords = await rowHooks.reduce(async (recordsPromise: Promise<any[]>, hook) => {
                const currentRecords = await recordsPromise;
                return Promise.all(currentRecords.map(hook));
            }, Promise.resolve(records));

            onResults(processedRecords);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to parse CSV file";
            onError?.(errorMessage);
            posthog.capture(`import_${importIdentifier}_failed`);
        } finally {
            setIsLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                disabled={isLoading}
            />
            <button
                type="button"
                className={`audette-button ${importButtonStyle}`}
                onClick={handleClick}
                disabled={isLoading}
                style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? "not-allowed" : "pointer" }}
            >
                <ImportIcon />
                {isLoading ? "Importing..." : "Import"}
            </button>
        </>
    );
}

export default SimpleCsvUploader;
